/**
 * @file   src\Pages\CWOBankInformation.tsx
 * @brief  BankInformation page.
 * @date   JUL, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */

import { Container, Breadcrumb, Button, Row, Col, Form, useOutletContext, useNavigate, useEffect, useState, useLocation, useRef } from '../utils/thirdpartyComponents';
import CWSelect from '../components/CWSelect';
import CWInput from '../components/CWInput';
import CWRadio from '../components/CWRadio';
import { AppContextType, COBankData } from '../interfaces/generalInterface';
import { CobankingInfoSchema, ColocationbankingInfoSchema } from '../validations/CobankingInfoSchema';
import * as Yup from 'yup'
import { pageURLs } from '../utils/pageUrls';
import { PaymentType } from '../utils/enums';
import { ACCOUNT_NAME_MAX_LENGTH, ACCOUNT_NUMBER_MAX_LENGTH, ROUTING_NUMBER_MAX_LENGTH } from '../utils/constants';

const CWOBankInformation = () => {
    // Access the context
    const { data, updateData } = useOutletContext<AppContextType>();
    useEffect(() => {
        handleUpdate()
    }, [])

    // Update the data
    const handleUpdate = () => {
        updateData({ heading: "Bank Information", url: "", showBackButton: false, showMainMenu: true, activeLink: "" });
    };

    const [options, setOptions] = useState([
        { label: 'Location 1', value: 1 },
        { label: 'Location 2', value: 2 },
    ]);

    // Convert PaymentType enum to an array of options
    const paymentOptions = [
        { label: 'Select', value: 0 }, // First option with value 0
        ...Object.keys(PaymentType).filter(key => !isNaN(Number(key))).map(key => ({
            label: PaymentType[key as keyof typeof PaymentType],
            value: Number(key)
        }))
    ];

    //State to handle form data
    const [formData, setFormData] = useState<COBankData>({
        information: true,
        washlocation: 0,
        paymentType: 1,
        accountname: '',
        accountnumber: '',
        retypeaccountnumber: '',
        routingnumber: ''
    });

    // State to handle errors
    const [errors, setErrors] = useState<any>({});

    //State to handle location form data
    const [locationformData, setLocationFormData] = useState<COBankData>({
        information: true,
        washlocation: 1,
        paymentType: 1,
        accountname: '',
        accountnumber: '',
        retypeaccountnumber: '',
        routingnumber: ''
    });

    // State to handle location errors
    const [locationerrors, setLocationErrors] = useState<any>({});


    const refs = useRef<{ [key: string]: HTMLInputElement | HTMLTextAreaElement | null }>({});

    // On input change
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        // To allow only digits in routingnumber field
        if (e.target.name === "routingnumber") {
            // Extract only digits from the input value
            const routingnumber_value = e.target.value;
            if (!/^\d*$/.test(routingnumber_value)) {
                e.target.value = routingnumber_value.replace(/\D/g, '');
            }
        }

        // To allow only digits in accountnumber field
        if (e.target.name === "accountnumber") {
            // Extract only digits from the input value
            const accountnumber_value = e.target.value;
            if (!/^\d*$/.test(accountnumber_value)) {
                e.target.value = accountnumber_value.replace(/\D/g, '');
            }
        }

        // To allow only digits in retypeaccountnumber field
        if (e.target.name === "retypeaccountnumber") {
            // Extract only digits from the input value
            const retypeaccountnumber_value = e.target.value;
            if (!/^\d*$/.test(retypeaccountnumber_value)) {
                e.target.value = retypeaccountnumber_value.replace(/\D/g, '');
            }
        }

        const { name, value } = e.target;

        // To remove validation when user enters input
        var keys = Object.keys(errors);
        if (keys.includes(name)) {
            setErrors({
                ...errors,
                [name]: ""
            })
        }

        const newFormData = {
            ...formData,
            [name]: value,
        };

        setFormData(newFormData);
    };

    const onChangeAction = (value: boolean) => {

        const newFormData = {
            ...formData,
            'information': value,
        };
        const newFormData2 = {
            ...locationformData,
            'information': value,
        };

        setFormData(newFormData);
        setLocationFormData(newFormData2);
    };

    // Triggers whenever options is selected
    const handleSelectChange = (selectedOption: any, name: string) => {
        const value = selectedOption?.value || '';

        // To remove validation when user enters input
        var keys = Object.keys(errors);
        if (keys.includes(name)) {
            setErrors({
                ...errors,
                [name]: ""
            })
        }

        const newFormData = {
            ...formData,
            [name]: value,
        };
        setFormData(newFormData);
    };

    //To validate form on clicking next
    const validateForm = async (): Promise<boolean> => {
        try {
            await CobankingInfoSchema.validate(formData, { abortEarly: false });
            setErrors({});
            alert('Not Implemented');
            return true;
        } catch (error) {
            if (error instanceof Yup.ValidationError) {
                const newErrors: any = {};
                const errorFields: string[] = [];

                // This is done to get the errors in order
                const fieldOrder = ['accountname', 'accountnumber', 'retypeaccountnumber', 'routingnumber', 'paymentType'];

                error.inner.forEach((err: any) => {
                    if (!newErrors[err.path]) {
                        newErrors[err.path] = err.message;
                        if (!errorFields.includes(err.path)) {
                            errorFields.push(err.path);
                        }
                    }
                });

                setErrors(newErrors);

                // Find the first field with error in the predefined order
                const firstErrorField = fieldOrder.find(field => errorFields.includes(field));
                const element = refs.current[firstErrorField || ''];
                if (element) {
                    element.focus();
                }
            }
            return false
        }
    };



    // location information


    // On input change
    const locationhandleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        // To allow only digits in routingnumber field
        if (e.target.name === "routingnumber") {
            // Extract only digits from the input value
            const routingnumber_value = e.target.value;
            if (!/^\d*$/.test(routingnumber_value)) {
                e.target.value = routingnumber_value.replace(/\D/g, '');
            }
        }

        // To allow only digits in accountnumber field
        if (e.target.name === "accountnumber") {
            // Extract only digits from the input value
            const accountnumber_value = e.target.value;
            if (!/^\d*$/.test(accountnumber_value)) {
                e.target.value = accountnumber_value.replace(/\D/g, '');
            }
        }

        // To allow only digits in retypeaccountnumber field
        if (e.target.name === "retypeaccountnumber") {
            // Extract only digits from the input value
            const retypeaccountnumber_value = e.target.value;
            if (!/^\d*$/.test(retypeaccountnumber_value)) {
                e.target.value = retypeaccountnumber_value.replace(/\D/g, '');
            }
        }

        const { name, value } = e.target;

        // To remove validation when user enters input
        var keys = Object.keys(errors);
        if (keys.includes(name)) {
            setLocationErrors({
                ...locationerrors,
                [name]: ""
            })
        }

        const newFormData = {
            ...locationformData,
            [name]: value,
        };

        setLocationFormData(newFormData);
    };

    // Triggers whenever options is selected
    const locationhandleSelectChange = (selectedOption: any, name: string) => {
        const value = selectedOption?.value || '';

        // To remove validation when user enters input
        var keys = Object.keys(locationerrors);
        if (keys.includes(name)) {
            setLocationErrors({
                ...errors,
                [name]: ""
            })
        }
        if (name === "washlocation") {
            const newlocationformData = ({
                information: false,
                washlocation: value,
                paymentType: 1,
                accountname: 'John Doe 2',
                accountnumber: '999123456798',
                retypeaccountnumber: '999123456798',
                routingnumber: '110000000'
            });
            const newlocationformData1 = ({
                information: true,
                washlocation: 1,
                paymentType: 1,
                accountname: 'John Doe L',
                accountnumber: '000123456798',
                retypeaccountnumber: '000123456798',
                routingnumber: '110000000'
            });
            if (value === 2) {
                setLocationFormData(newlocationformData);
            }
            else if (value === 1) {
                setLocationFormData(newlocationformData1);
            }
        }
        else {
            const newFormData = {
                ...locationformData,
                [name]: value,
            };
            setLocationFormData(newFormData);

        }

    };

    //To validate location form on clicking next
    const locationvalidateForm = async (): Promise<boolean> => {
        try {
            await ColocationbankingInfoSchema.validate(locationformData, { abortEarly: false });
            setLocationErrors({});
            alert('Not Implemented');
            return true;
        } catch (locationerror) {
            if (locationerror instanceof Yup.ValidationError) {
                const newErrors: any = {};
                const errorFields: string[] = [];

                // This is done to get the errors in order
                const fieldOrder = ['accountname', 'accountnumber', 'retypeaccountnumber', 'routingnumber', 'paymentType'];

                locationerror.inner.forEach((err: any) => {
                    if (!newErrors[err.path]) {
                        newErrors[err.path] = err.message;
                        if (!errorFields.includes(err.path)) {
                            errorFields.push(err.path);
                        }
                    }
                });

                setLocationErrors(newErrors);

                // Find the first field with error in the predefined order
                const firstErrorField = fieldOrder.find(field => errorFields.includes(field));
                const element = refs.current[firstErrorField || ''];
                if (element) {
                    element.focus();
                }
            }
            return false
        }
    };

    return (

        <div className='hardware-listing-container addnew-item-wrap'>
            <Container>
                <div className="hardware-list-wrap">
                    <div className="form-inner mx-auto">
                        <div>
                            <div className="radio-field-group d-flex">
                                <CWRadio
                                    id="carwashinformation"
                                    name="information"
                                    label="Car Wash Information"
                                    checked={formData.information}
                                    onChange={() => onChangeAction(true)}
                                />
                                <CWRadio
                                    id="locationinformation"
                                    name="information"
                                    label="Location Information"
                                    checked={!formData.information}
                                    onChange={() => onChangeAction(false)}
                                />
                            </div>
                        </div>

                        {formData.information && (
                            <>

                                <h5 className='title f-bold'>Select Payment Type</h5>
                                <Row>
                                    <Col md={6}>
                                        <CWSelect
                                            id="paymentType"
                                            name="paymentType"
                                            options={paymentOptions}
                                            label="Select Merchant Payment Type"
                                            errorMessage={errors.paymentType && errors.paymentType}
                                            dropdownGap={0}
                                            dropdownPosition="auto"
                                            values={paymentOptions.filter(option => option.value === formData.paymentType)}
                                            onChange={(selectedOptions) => handleSelectChange(selectedOptions[0], 'paymentType')}
                                            placeholder="Select Merchant Payment Type"
                                            multi={false}
                                            searchable={false}
                                            isMandatory
                                        />
                                    </Col>
                                </Row>
                                <h5 className='title f-bold'>Account Information</h5>
                                <Row>
                                    <Col md={6}>
                                        <CWInput
                                            id="accountname"
                                            name="accountname"
                                            type="text"
                                            label="Account Name"
                                            value={formData.accountname}
                                            onChange={handleChange}
                                            errorMessage={errors.accountname}
                                            maxLength={ACCOUNT_NAME_MAX_LENGTH}
                                            isMandatory
                                            ref={(el) => (refs.current.accountname = el)}

                                        />
                                    </Col>
                                    <Col md={6}>
                                        <CWInput
                                            id="accountnumber"
                                            name="accountnumber"
                                            type="text"
                                            label="Account Number"
                                            value={formData.accountnumber}
                                            onChange={handleChange}
                                            errorMessage={errors.accountnumber}
                                            maxLength={ACCOUNT_NUMBER_MAX_LENGTH}
                                            ref={(el) => (refs.current.accountnumber = el)}
                                            autoComplete="off"
                                            isMandatory
                                        />
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={6}>
                                        <CWInput
                                            id="retypeaccountnumber"
                                            name="retypeaccountnumber"
                                            type="text"
                                            label="Re-Type Account Number"
                                            value={formData.retypeaccountnumber}
                                            onChange={handleChange}
                                            errorMessage={errors.retypeaccountnumber}
                                            maxLength={ACCOUNT_NUMBER_MAX_LENGTH}
                                            ref={(el) => (refs.current.retypeaccountnumber = el)}
                                            autoComplete="off"
                                            isMandatory
                                            onCopy={(e) => e.preventDefault()}
                                            onCut={(e) => e.preventDefault()}
                                            onPaste={(e) => e.preventDefault()}
                                        />
                                    </Col>
                                    <Col md={6}>
                                        <CWInput
                                            id="routingnumber"
                                            name="routingnumber"
                                            type="text"
                                            label="Routing Number"
                                            value={formData.routingnumber}
                                            onChange={handleChange}
                                            errorMessage={errors.routingnumber}
                                            maxLength={ROUTING_NUMBER_MAX_LENGTH}
                                            ref={(el) => (refs.current.routingnumber = el)}
                                            autoComplete="off"
                                            isMandatory
                                        />
                                    </Col>
                                </Row>
                                <div className="btn-sec d-flex justify-content-end">
                                    <Button title="Cancel" variant="primary" className="secondary-btn">Cancel</Button>
                                    <Button title="Save" variant="primary" className="primary-btn" type="button" onClick={validateForm}>Save</Button>
                                </div>
                            </>
                        )}
                        {!formData.information && (
                            <>
                                <h5 className='title f-bold'>Select Location</h5>
                                <Row>
                                    <Col md={12}>
                                        <CWSelect
                                            id="washlocation"
                                            name="washlocation"
                                            options={options}
                                            values={options.filter(option => option.value === locationformData.washlocation)}
                                            onChange={(selectedOptions) => locationhandleSelectChange(selectedOptions[0], 'washlocation')}
                                            label="Select Car Wash Location"
                                            errorMessage={locationerrors.washlocation && locationerrors.washlocation}
                                            dropdownGap={0}
                                            dropdownPosition="auto"
                                            placeholder="Select"
                                            multi={false}
                                            isMandatory
                                            className="no-input"
                                        />
                                    </Col>
                                </Row>
                                <h5 className='title f-bold'>Select Payment Type</h5>
                                <Row>
                                    <Col md={6}>
                                        <CWSelect
                                            id="paymentType"
                                            name="paymentType"
                                            options={paymentOptions}
                                            label="Select Merchant Payment Type"
                                            errorMessage={locationerrors.paymentType && locationerrors.paymentType}
                                            dropdownGap={0}
                                            dropdownPosition="auto"
                                            values={paymentOptions.filter(option => option.value === formData.paymentType)}
                                            onChange={(selectedOptions) => locationhandleSelectChange(selectedOptions[0], 'paymentType')}
                                            placeholder="Select Merchant Payment Type"
                                            multi={false}
                                            searchable={false}
                                            isMandatory
                                        />
                                    </Col>
                                </Row>
                                <h5 className='title f-bold'>Account Information</h5>
                                <Row>
                                    <Col md={6}>
                                        <CWInput
                                            id="accountname"
                                            name="accountname"
                                            type="text"
                                            label="Account Name"
                                            value={locationformData.accountname}
                                            onChange={locationhandleChange}
                                            errorMessage={locationerrors.accountname}
                                            maxLength={ACCOUNT_NAME_MAX_LENGTH}
                                            isMandatory
                                            ref={(el) => (refs.current.accountname = el)}

                                        />
                                    </Col>
                                    <Col md={6}>
                                        <CWInput
                                            id="accountnumber"
                                            name="accountnumber"
                                            type="text"
                                            label="Account Number"
                                            value={locationformData.accountnumber}
                                            onChange={locationhandleChange}
                                            errorMessage={locationerrors.accountnumber}
                                            maxLength={ACCOUNT_NUMBER_MAX_LENGTH}
                                            ref={(el) => (refs.current.accountnumber = el)}
                                            autoComplete="off"
                                            isMandatory
                                        />
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={6}>
                                        <CWInput
                                            id="retypeaccountnumber"
                                            name="retypeaccountnumber"
                                            type="text"
                                            label="Re-Type Account Number"
                                            value={locationformData.retypeaccountnumber}
                                            onChange={locationhandleChange}
                                            errorMessage={locationerrors.retypeaccountnumber}
                                            maxLength={ACCOUNT_NUMBER_MAX_LENGTH}
                                            ref={(el) => (refs.current.retypeaccountnumber = el)}
                                            autoComplete="off"
                                            isMandatory
                                            onCopy={(e) => e.preventDefault()}
                                            onCut={(e) => e.preventDefault()}
                                            onPaste={(e) => e.preventDefault()}
                                        />
                                    </Col>
                                    <Col md={6}>
                                        <CWInput
                                            id="routingnumber"
                                            name="routingnumber"
                                            type="text"
                                            label="Routing Number"
                                            value={locationformData.routingnumber}
                                            onChange={locationhandleChange}
                                            errorMessage={locationerrors.routingnumber}
                                            maxLength={ROUTING_NUMBER_MAX_LENGTH}
                                            ref={(el) => (refs.current.routingnumber = el)}
                                            autoComplete="off"
                                            isMandatory
                                        />
                                    </Col>
                                </Row>
                                <div className="btn-sec d-flex justify-content-end">
                                    <Button title="Cancel" variant="primary" className="secondary-btn">Cancel</Button>
                                    <Button title="Save" variant="primary" className="primary-btn" type="button" onClick={locationvalidateForm}>Save</Button>
                                </div>
                            </>
                        )}

                    </div>
                </div>
            </Container >
        </div >
    )
}

export default CWOBankInformation;
